/**
 * @file src/multi/stageArrangement.js
 * @description Pure arrangement math for the multi-stage Orbiter Studio's arrange UX (card drawer +
 * drag-to-stage + reorder). No DOM, no React, no realm — just the decisions the arrange surface needs,
 * kept framework-free so they are one source of truth and unit-testable:
 *
 *   - `buildDesiredArrangement` — the entry that SHOULD occupy each visible stage: a stage holds a voice
 *     ONLY when the user has explicitly placed one there (a dragged card). Stages start EMPTY (a
 *     placeholder), exactly like root's mixed-collection — nothing is auto-loaded from the collection; the
 *     collection only populates the drawer of cards the user drags from.
 *   - `planReconcile` — given the live stage→voice map and that desired arrangement, compute the minimal
 *     `{ removes, adds }` to converge the realm. Two passes (remove-then-add) so untouched stages keep
 *     their voice (no audio cut for survivors) and a replaced stage frees before it re-adds.
 *   - `moveEntryBefore` — reorder the drawer's entry list (drag one card before another / to the end).
 *   - `cruiseResumeIndex` — where the auto-advance carries on from when a track ends.
 *   - `swapDescriptor` — the session descriptor for an in-place swap: a card dropped on an OCCUPIED
 *     stage replaces only its own dimension (track / orbiter / world) of that stage's live session.
 *   - `planRestoredArrangement` — the rail order and stage placements a SAVED arrangement asks for,
 *     resolved against the collection as it stands today.
 *
 * Duplicates are allowed (product decision): the same collection entry can play on two stages at once.
 * Each explicit placement carries its OWN `voiceId` (minted by the caller over the entry's shared audio
 * ids), so two live copies never collide on the realm's voiceId key. This math therefore does NOT dedupe
 * by entry — it trusts that every placement's `voiceId` is already distinct.
 */

/**
 * The entry that SHOULD occupy each of `target` stages. A stage is filled ONLY by an explicit placement
 * (a dragged card); with no placement it is EMPTY (a placeholder). Nothing is auto-loaded — growing the
 * count reveals empty placeholders, and a fresh collection shows all placeholders until the user drags.
 * @param {Map<number, {voiceId:string}|null>} overrides explicit stage placements — a placement entry
 *   (its own minted `voiceId`), or `null`/absent for an empty stage.
 * @param {number} target visible stage count.
 * @returns {Array<{voiceId:string}|null>} the add-ready entry per stage index (length `target`).
 */
export function buildDesiredArrangement(overrides, target) {
  const desired = [];
  for (let i = 0; i < target; i++) desired.push(overrides.get(i) ?? null);
  return desired;
}

/**
 * Compute the minimal realm mutations to make each stage hold its desired entry.
 * @param {Array<string|null>} slotVoiceIds live stage index → occupying voiceId (null = empty stage).
 * @param {Array<{voiceId:string}|null>} desiredEntries stage index → the entry that SHOULD occupy it
 *   (null = the stage should be empty). Its length is the target visible stage count.
 * @returns {{ removes: Array<{index:number, voiceId:string}>, adds: Array<{index:number, entry:object}> }}
 *   `removes` = voices whose stage no longer wants them (changed or beyond the target); apply these first.
 *   `adds` = stages whose desired entry isn't live yet; apply after the removes (so a replaced slot is free).
 */
export function planReconcile(slotVoiceIds, desiredEntries) {
  const target = desiredEntries.length;
  const removes = [];
  for (let i = 0; i < slotVoiceIds.length; i++) {
    const cur = slotVoiceIds[i];
    if (!cur) continue;
    const desiredId = i < target ? desiredEntries[i]?.voiceId ?? null : null;
    if (cur !== desiredId) removes.push({ index: i, voiceId: cur });
  }
  const adds = [];
  for (let i = 0; i < target; i++) {
    const entry = desiredEntries[i];
    if (entry && slotVoiceIds[i] !== entry.voiceId) adds.push({ index: i, entry });
  }
  return { removes, adds };
}

/**
 * The full session descriptor for dropping `entry` onto a stage whose live session is `current`
 * (the voice's resolved config request). The card's OWN dimension is classified by id precedence
 * (track → orbiter → world, the same order the roster uses); only that dimension changes — the other
 * two are carried over EXPLICITLY from `current` (the engine's descriptor merge only overwrites
 * truthy fields, and an absent id would be back-filled from the new track's defaults instead of
 * keeping what is playing). Surviving dimensions also pin their CURRENT versions in `requested` so
 * they re-resolve byte-identical (warm cache hits, no accidental version upgrade mid-swap); the
 * swapped dimension takes the incoming card's version when it carries one. Online cards normally
 * carry no version and still request latest; an offline card carries the release the download
 * stored, and dropping that pin would bypass the primed cache and reach for a network that is not
 * there.
 * @param {{trackId?:string, trackVersion?:(string|null), orbiterId?:(string|null),
 *   orbiterVersion?:(string|null), entangledWorldId?:(string|null),
 *   entangledWorldVersion?:(string|null)}|null} current the stage's live resolved request.
 * @param {{trackId?:(string|null), orbiterId?:(string|null), entangledWorldId?:(string|null)}} entry
 *   the dropped card.
 * @returns {{dim:('track'|'orbiter'|'world'), session:object, requested:object, noop:boolean}|null}
 *   null when the card carries no swappable dimension or the stage has no resolved track to swap
 *   against; `noop` when the swap would land the exact triad already playing.
 */
export function swapDescriptor(current, entry) {
  if (!current?.trackId) return null;
  const dim = entry?.trackId ? 'track' : entry?.orbiterId ? 'orbiter' : entry?.entangledWorldId ? 'world' : null;
  if (!dim) return null;

  const session = {
    trackId: dim === 'track' ? entry.trackId : current.trackId,
    orbiterId: dim === 'orbiter' ? entry.orbiterId : current.orbiterId ?? null,
    entangledWorldId: dim === 'world' ? entry.entangledWorldId : current.entangledWorldId ?? null,
  };
  const requested = {
    trackVersion: dim === 'track' ? entry.trackVersion ?? null : current.trackVersion ?? null,
    orbiterVersion: dim === 'orbiter' ? entry.orbiterVersion ?? null : current.orbiterVersion ?? null,
    entangledWorldVersion:
      dim === 'world' ? entry.entangledWorldVersion ?? null : current.entangledWorldVersion ?? null,
  };
  const incomingVersion =
    dim === 'track'
      ? entry.trackVersion
      : dim === 'orbiter'
        ? entry.orbiterVersion
        : entry.entangledWorldVersion;
  const currentVersion =
    dim === 'track'
      ? current.trackVersion
      : dim === 'orbiter'
        ? current.orbiterVersion
        : current.entangledWorldVersion;
  // An unversioned online card still means “this entity”, so an identical id is a no-op. A
  // downloaded card says “this entity AT this release”; treating an id match with a version
  // mismatch as a no-op would leave the stage on bytes other than the ones it stored.
  const requestedReleaseAlreadyLoaded =
    incomingVersion === null
    || incomingVersion === undefined
    || String(incomingVersion) === String(currentVersion);
  const noop =
    session.trackId === current.trackId &&
    session.orbiterId === (current.orbiterId ?? null) &&
    session.entangledWorldId === (current.entangledWorldId ?? null) &&
    requestedReleaseAlreadyLoaded;
  return { dim, session, requested, noop };
}

/**
 * Reorder `entries` so `sourceVoiceId` sits immediately before `beforeVoiceId` (or at the end when
 * `beforeVoiceId` is null). Returns the SAME array reference when nothing moved, so callers can skip a
 * no-op update. Ported from root's `moveEntryBefore` (keyed by `voiceId` instead of `itemId`).
 * @param {Array<{voiceId:string}>} entries
 * @param {string} sourceVoiceId
 * @param {string|null} beforeVoiceId
 */
export function moveEntryBefore(entries, sourceVoiceId, beforeVoiceId) {
  const sourceIndex = entries.findIndex((e) => e.voiceId === sourceVoiceId);
  if (sourceIndex < 0) return entries;

  const next = [...entries];
  const [source] = next.splice(sourceIndex, 1);
  if (!source) return entries;

  if (beforeVoiceId === null) {
    next.push(source);
    return next;
  }
  const targetIndex = next.findIndex((e) => e.voiceId === beforeVoiceId);
  if (targetIndex < 0) {
    next.push(source);
    return next;
  }
  next.splice(targetIndex, 0, source);
  return next;
}


/**
 * Where the cruise carries on from when the track on a stage ends: the rail index to start scanning
 * for the next bootable card.
 *
 * Normally that is one past the playing card. The case worth naming is the card REMOVED from the
 * collection while it was playing: removal edits the list, it does not stop the music, so the card
 * plays on from its stage while no longer being in the rail. Its `currentIndex` is then -1, and
 * reading that as "the end of the rail" would silently end the cruise at that track.
 *
 * The caller resolves the card that FOLLOWED it and passes that card's current index — an identity,
 * resolved fresh, not a position remembered from the moment of removal. A remembered position goes
 * stale the moment anything else is removed or reordered, and a stale position lands the cruise on the
 * wrong track or ends it early.
 *
 * @param {number} currentIndex Index of the playing card in the rail, or -1 when it is no longer there.
 * @param {number} followerIndex Index of the card that followed it, or -1 when there is none left.
 * @param {number} length The rail's current length.
 * @returns {number} The index to scan from — `length` when there is nothing left to play.
 */
export function cruiseResumeIndex(currentIndex, followerIndex, length) {
  if (currentIndex >= 0) return currentIndex + 1;
  if (typeof followerIndex !== 'number' || followerIndex < 0) return length;
  return Math.min(followerIndex, length);
}


/**
 * What a SAVED arrangement asks for, resolved against the roster as it stands NOW.
 *
 * A saved layout names collection items by their source id, and the collection goes on changing
 * after it is written: items are removed, items are added, and the rule for what may seed a stage
 * has itself changed (orbiter and world cards became swap-only, so only a track can start one). So
 * this resolves rather than trusts — every saved id is looked up in the live roster, and anything
 * that no longer resolves, or no longer qualifies, is dropped quietly. A stale arrangement degrades
 * into a smaller one; it never restores something that is not there.
 *
 * Pure: it mints no voiceIds and mutates nothing. The caller mints each placement's realm id exactly
 * as a drag-to-stage would, so a restored stage and a just-dropped one are indistinguishable.
 *
 * `swaps` are the stages that were playing something other than their card's own pairing. A stage
 * is a TRIAD — a track, an orbiter and a world — and a card names only one of the three, so without
 * these a stage restores to its track plus the author's default pairing rather than what was set up
 * on it. Applied by OVERLAYING the swapped ids onto the entry the card resolves to, which is exactly
 * the shape a live swap produces, so a restored stage and a hand-swapped one boot identically.
 *
 * @param {{order?:Array<string|null>, entryOrder?:string[],
 *   swaps?:Array<{index:number, orbiterId?:string, entangledWorldId?:string}>}|null} savedLayout
 * @param {Array<{voiceId:string}>} roster the collection's live entries, in collection order.
 * @param {(entry:object) => boolean} isBootable whether an entry may SEED a stage (vs. only swap into one).
 * @returns {{entries:Array<object>, placements:Array<{index:number, entry:object}>, stages:number}}
 *   `entries` = the rail in its restored order (the roster unchanged when nothing was remembered);
 *   `placements` = stage index → the entry to seed it with, holes omitted;
 *   `stages` = the smallest stage count that shows every placement (0 when there are none).
 */
export function planRestoredArrangement(savedLayout, roster, isBootable) {
  const entries = Array.isArray(roster) ? roster : [];
  const empty = { entries, placements: [], stages: 0 };
  if (!savedLayout || typeof savedLayout !== 'object') return empty;

  const bySourceId = new Map(entries.map((entry) => [entry.voiceId, entry]));

  // The rail. Entries the saved order does not mention were ADDED since it was written; the
  // arrangement says nothing about where they belong, so they keep their collection order among
  // themselves and follow the remembered ones rather than being interleaved by guesswork.
  const savedEntryOrder = Array.isArray(savedLayout.entryOrder) ? savedLayout.entryOrder : [];
  const placed = new Set();
  const remembered = [];
  for (const sourceId of savedEntryOrder) {
    const entry = sourceId ? bySourceId.get(sourceId) : null;
    if (!entry || placed.has(entry.voiceId)) continue;
    placed.add(entry.voiceId);
    remembered.push(entry);
  }
  const restoredEntries = remembered.length
    ? [...remembered, ...entries.filter((entry) => !placed.has(entry.voiceId))]
    : entries;

  // The stages. `order` is POSITIONAL — index = stage, null = a stage deliberately left empty — so
  // it is walked by index and its holes are honoured rather than closed up. Closing them would
  // slide every later stage down one and restore an arrangement nobody made.
  const savedOrder = Array.isArray(savedLayout.order) ? savedLayout.order : [];
  // Stage index → the pairing that stage was left playing, when it is not its card's own.
  const swapByIndex = new Map(
    (Array.isArray(savedLayout.swaps) ? savedLayout.swaps : [])
      .filter((swap) => swap && Number.isInteger(swap.index))
      .map((swap) => [swap.index, swap]),
  );
  const placements = [];
  let stages = 0;
  savedOrder.forEach((sourceId, index) => {
    const entry = sourceId ? bySourceId.get(sourceId) : null;
    if (!entry || !isBootable(entry)) return;
    const swap = swapByIndex.get(index);
    // Overlaid onto a COPY: the roster entry is the card as the collection holds it, and the rail
    // must go on showing that card rather than one stage's version of it. Only the dimensions the
    // swap actually names are replaced, so a stage that swapped an orbiter keeps the card's world.
    const placed = swap
      ? {
        ...entry,
        ...(swap.orbiterId ? { orbiterId: swap.orbiterId, orbiterVersion: null } : {}),
        ...(swap.entangledWorldId
          ? { entangledWorldId: swap.entangledWorldId, entangledWorldVersion: null }
          : {}),
      }
      : entry;
    placements.push({ index, entry: placed });
    stages = index + 1;
  });

  return { entries: restoredEntries, placements, stages };
}
